import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Container, Group, Button, Title, Text, Card, SimpleGrid, Box, Image, Accordion } from '@mantine/core'
import { IconArrowRight, IconSparkles, IconPhoto, IconVideo, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { api } from '../api/client'
import { LOGO_URL, BRAND_NAME, BRAND_SUBLINE } from '../branding'
import SiteFooter from '../components/SiteFooter'
import { Seo, DEFAULT_OG_IMAGE } from '../components/Seo'
import useEmblaCarousel from 'embla-carousel-react'
import AutoScroll from 'embla-carousel-auto-scroll'
import marketWb from '../assets/market_wb.svg'
import marketOzon from '../assets/market_ozon.svg'
import marketYandex from '../assets/market_yandex.svg'
import marketAvito from '../assets/market_avito.svg'

// === Auto-injected: Reveal on Scroll Hook ===
function useRevealOnScroll() {
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll<HTMLElement>(
      '.section-header, .grid-card, .cta-card, .spotlight-card, .example-card, .how-card, .pack-card, .reveal'
    ));
    // Add baseline reveal classes if missing
    targets.forEach(el => {
      if (!el.classList.contains('reveal')) {
        el.classList.add('reveal', 'reveal--up');
        if (el.classList.contains('cta-card') || el.classList.contains('spotlight-card')) {
          el.classList.add('reveal--scale');
        }
      }
    });

    const hasIO = 'IntersectionObserver' in window;
    if (!hasIO) {
      targets.forEach(el => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).classList.add('is-inview');
          obs.unobserve(entry.target);
        }
      });
    }, { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    targets.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}
// === /Auto-injected: Reveal on Scroll Hook ===


// --- Helpers ---
const TOPBAR_OFFSET = 96 // px: sticky header + safe gap
const EXAMPLES_MARGIN = 24 // px: sticky header + safe gap
function Section({ children, id, py = 'xl' as any, className, style, fluid, size = 'lg' as any }: { children: React.ReactNode; id?: string; py?: any; className?: string; style?: React.CSSProperties; fluid?: boolean; size?: any }) {
  const sectionClassName = ['landing-section', className].filter(Boolean).join(' ')
  return (
    <Box id={id} component="section" py={py} className={sectionClassName} style={{ scrollMarginTop: TOPBAR_OFFSET, ...(style ?? {}) }}>
      <Container size={fluid ? undefined : size} fluid={fluid}>{children}</Container>
    </Box>
  )
}
type AccentTone = 'neutral' | 'iris' | 'sunrise' | 'lagoon'

const TOPBAR_LINKS = [
  { href: '#tools', label: 'Возможности' },
  { href: '#examples', label: 'Примеры' },
  { href: '#packs', label: 'Цены' },
] as const

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = () => setPrefersReducedMotion(media.matches)
    handler()
    if (media.addEventListener) {
      media.addEventListener('change', handler)
      return () => media.removeEventListener('change', handler)
    }
    media.addListener(handler)
    return () => media.removeListener(handler)
  }, [])
  return prefersReducedMotion
}

function CountUpValue({ value, durationMs = 1100 }: { value: string; durationMs?: number }) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [display, setDisplay] = useState(value)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const match = String(value).match(/-?\d+(?:[.,]\d+)?/)
    if (!match) {
      setDisplay(value)
      return
    }
    const numberText = match[0]
    const target = Number(numberText.replace(',', '.'))
    const prefix = value.slice(0, match.index ?? 0)
    const suffix = value.slice((match.index ?? 0) + numberText.length)

    if (!Number.isFinite(target) || prefersReducedMotion) {
      setDisplay(value)
      return
    }

    const start = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs)
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(target * eased)
      setDisplay(`${prefix}${current}${suffix}`)
      if (progress < 1) {
        rafRef.current = window.requestAnimationFrame(step)
      }
    }
    rafRef.current = window.requestAnimationFrame(step)
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current)
    }
  }, [value, durationMs, prefersReducedMotion])

  return <span>{display}</span>
}

function Topbar({ solid }: { solid: boolean }) {
  return (
    <Box component="header" className={`topbar ${solid ? 'topbar--solid' : 'topbar--transparent'}`}>
      <Container size="lg" py="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Group gap="xs" align="center">
              <Image src={LOGO_URL} alt={`${BRAND_NAME} logo`} w={28} h={28} fit="contain" style={{ display: 'block' }} />
              <div className="brand-lockup">
                <span className="brand-wordmark">{BRAND_NAME}</span>
                <span className="brand-subline brand-subline--topbar">{BRAND_SUBLINE}</span>
              </div>
            </Group>
          </Link>
          <Group gap="xs" visibleFrom="sm" className="topbar-nav">
            {TOPBAR_LINKS.map((item) => (
              <Button
                key={item.href}
                component="a"
                href={item.href}
                variant="subtle"
                size="sm"
                className="topbar-link"
              >
                {item.label}
              </Button>
            ))}
          </Group>
          <Group>
            <Button component={Link} to="/auth" radius="xl" size="sm" className="topbar-cta">
              <span className="topbar-cta__full">Попробовать бесплатно</span>
              <span className="topbar-cta__short">Попробовать</span>
            </Button>
          </Group>
        </Group>
      </Container>
    </Box>
  )
}

const HERO_STATS = [
  { value: '65x', label: 'экономия денег и времени' },
  { value: '99%', label: 'точности итогового результата' },
  { value: '45', label: 'секунд до готового результата' },
]

type MarketLogo = { name: string; color: string; short: string; iconUrl?: string }
const MARKET_LOGOS: MarketLogo[] = [
  { name: 'Wildberries', color: '#a855f7', short: 'WB', iconUrl: marketWb },
  { name: 'Ozon', color: '#2563eb', short: 'OZ', iconUrl: marketOzon },
  { name: 'Яндекс.Маркет', color: '#facc15', short: 'ЯМ', iconUrl: marketYandex },
  { name: 'Авито', color: '#10b981', short: 'SM', iconUrl: marketAvito },
]

const LANDING_KEYWORDS = [
  'генерация карточек товаров',
  'инфографика для маркетплейсов',
  'создание карточек товара',
  'ai инфографика',
  'дизайн карточек вайлдберриз',
  'оформление карточек озон',
  'виртуальная примерка одежды',
  'контент для Wildberries и Ozon',
  'сделать карточку товара онлайн',
]

const GENERATION_COST_CREDITS = 30
const IMAGE_GENERATION_COST_CREDITS = GENERATION_COST_CREDITS
const VIDEO_GENERATION_COST_CREDITS = 60

const LANDING_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fravai',
    url: 'https://fravai.com/',
    logo: DEFAULT_OG_IMAGE,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Fravai',
    url: 'https://fravai.com/',
    inLanguage: 'ru-RU',
    potentialAction: {
      '@type': 'RegisterAction',
      name: 'Регистрация в Fravai',
      target: 'https://fravai.com/auth',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Fravai',
    applicationCategory: 'DesignApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'RUB',
    },
    description: 'AI-сервис для создания карточек товаров и инфографики для маркетплейсов.',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '124',
    },
  },
]

const PREVIEW_BASE_URL = 'https://storage.yandexcloud.net/ai-previews/all/'
const previewUrl = (fileName: string) => `${PREVIEW_BASE_URL}${fileName}`

const DUNK_PREVIEWS: ShowcaseCardItem[] = [
  { title: 'Nike Dunk - стильные кеды', img: previewUrl('preview_dunk.webp') },
  { title: 'Nike Dunk - материалы', img: previewUrl('preview_dunk_1.webp') },
  { title: 'Nike Dunk - комфорт', img: previewUrl('preview_dunk_2.webp') },
  { title: 'Nike Dunk - прочность', img: previewUrl('preview_dunk_3.webp') },
  { title: 'Nike Dunk - городской стиль', img: previewUrl('preview_dunk_4.webp') },
  { title: 'Nike Dunk - карточка', img: previewUrl('preview_dunk_6.webp') },
]

const GAMEPAD_PREVIEWS: ShowcaseCardItem[] = [
  { title: 'Геймпад', img: previewUrl('preview_gamepad.webp') },
  { title: 'Геймпад - точность', img: previewUrl('preview_gamepad_1.webp') },
  { title: 'Геймпад - погружение', img: previewUrl('preview_gamepad_2.webp') },
  { title: 'Геймпад - управление', img: previewUrl('preview_gamepad_3.webp') },
  { title: 'Геймпад - комплектация', img: previewUrl('preview_gamepad_4.webp') },
  { title: 'Геймпад - комплект', img: previewUrl('preview_gamepad_5.webp') },
]

const MENDI_PREVIEWS: ShowcaseCardItem[] = [
  { title: 'Mendi - сокровище Индии', img: previewUrl('preview_mendi_1.webp') },
  { title: 'Mendi - идеальный рефреш', img: previewUrl('preview_mendi_2.webp') },
  { title: 'Mendi - формат', img: previewUrl('preview_mendi_3.webp') },
  { title: 'Mendi - вкус природы', img: previewUrl('preview_mendi_4.webp') },
  { title: 'Mendi - карточка', img: previewUrl('preview_mendi.webp') },
  { title: 'Mendi - свежесть', img: previewUrl('preview_mendi_5.webp') },
]

const examplesCarousel: ShowcaseCardItem[] = [
  { title: 'Инфографика 1', img: previewUrl('infographic_preview_1.webp') },
  { title: 'Инфографика 1', img: previewUrl('preview_dunk_pink.png') },
  { title: 'Инфографика 2', img: previewUrl('infographic_preview_2.webp') },
  { title: 'Инфографика 5', img: previewUrl('cross_product_result.webp') },
  { title: 'Инфографика 10', img: previewUrl('preview_mouse.webp') },
  { title: 'Инфографика 3', img: previewUrl('preview_bad.webp') },
  { title: 'Инфографика 3', img: previewUrl('preview_nike.webp') },
  { title: 'Умные часы', img: previewUrl('preview_watch.webp') },
  { title: 'Умные часы', img: previewUrl('preview_bads2.webp') },
  { title: 'Инфографика 9', img: previewUrl('infographic_preview_17.webp') },
  { title: 'Инфографика 4', img: previewUrl('infographic_preview_4.webp') },
  { title: 'Инфографика 5', img: previewUrl('infographic_preview_5.webp') },
  { title: 'Инфографика 7', img: previewUrl('infographic_preview_7.webp') },
  { title: 'Инфографика 9', img: previewUrl('infographic_preview_44.webp') },
]

type ShowcaseCardItem = { img: string; title: string }
type ElasticShowcase = {
  id: string
  title: string
  subtitle: string
  source: ShowcaseCardItem
  results: ShowcaseCardItem[]
}

const ELASTIC_SHOWCASES: ElasticShowcase[] = [
  {
    id: 'sneakers',
    title: 'Кроссовки',
    subtitle: 'В один клик из фото кроссовок раскладываем полноценную серию.',
    source: { title: 'Исходник: кроссовки', img: previewUrl('preview_dunk_input.webp') },
    results: DUNK_PREVIEWS,
  },
  {
    id: 'gaming',
    title: 'Техника',
    subtitle: 'В один клик из исходной фотографии геймпада получаем готовый продукт.',
    source: { title: 'Исходник: геймпад', img: previewUrl('preview_gamepad_input.webp') },
    results: GAMEPAD_PREVIEWS,
  },
  {
    id: 'drink',
    title: 'Напиток',
    subtitle: 'Один продукт превращается в серию карточек и инфографик под запуск.',
    source: { title: 'Исходник: напиток', img: previewUrl('preview_mendi_input.webp') },
    results: MENDI_PREVIEWS,
  },
]

const SERVICE_TOOLS = [
  {
    title: 'Карточка товара',
    label: 'Из фото в продающий визуал',
    desc: 'Загрузите товар и получите готовую обложку или баннер без фотостудии.',
    img: previewUrl('result_45.webp'),
  },
  {
    title: 'Инфографика',
    label: 'Преимущества на слайде',
    desc: 'Соберите понятные блоки про материал, размер, комплект и пользу товара.',
    img: previewUrl('preview_bads3.webp'),
  },
  {
    title: 'Серия слайдов',
    label: 'Единый стиль для SKU',
    desc: 'Сразу несколько карточек: обложка, детали, сценарии использования и финальный слайд.',
    img: previewUrl('product_slides_result_1.webp'),
  },
  {
    title: 'A/B вариант',
    label: 'Проверка гипотез',
    desc: 'Сделайте новую версию уже готовой карточки: другой фон, композиция или акцент.',
    img: previewUrl('preview_dunk_pink.png'),
  },
  {
    title: 'Улучшить готовую карточку',
    label: 'Если дизайн уже есть',
    desc: 'Загрузите текущую карточку и пересоберите ее аккуратнее, ярче или под новую акцию.',
    img: previewUrl('cross_product_result.webp'),
  },
  {
    title: 'Заменить товар',
    label: 'Стиль остается',
    desc: 'Подставьте другой товар в существующий дизайн, сохранив идею и окружение.',
    img: previewUrl('preview_mouse.webp'),
  },
  {
    title: 'Примерка на модели',
    label: 'Fashion без съемки',
    desc: 'Покажите одежду на модели, когда нет времени на студию, кастинг и ретушь.',
    img: previewUrl('result_33.webp'),
  },
  {
    title: 'Видео из изображения',
    label: 'Для рекламы и соцсетей',
    desc: 'Оживите карточку или товарный кадр для короткого промо и теста креатива.',
    img: previewUrl('preview_watch.webp'),
  },
] as const

const ENTRY_SCENARIOS = [
  { title: 'Есть только фото товара', desc: 'Начните с карточки, инфографики или серии слайдов.', tools: 'Фото товара, инфографика, серия' },
  { title: 'Есть готовая карточка', desc: 'Сделайте A/B вариант, набор слайдов или замените товар.', tools: 'Редактор, A/B, замена товара' },
  { title: 'Нужно оформить весь SKU', desc: 'Соберите набор слайдов в едином визуальном стиле.', tools: 'Серия слайдов' },
  { title: 'Нужно показать одежду', desc: 'Примерьте товар на модели без отдельной съемки.', tools: 'На модели, серия модели' },
  { title: 'Нужен рекламный контент', desc: 'Создайте баннер, вариант карточки или короткое видео.', tools: 'Баннер, A/B, видео' },
] as const

const STUDIO_HELPERS = [
  'определяет товар по фото',
  'предлагает сцену и композицию',
  'подсказывает акценты для карточки',
  'готовит настройки под результат',
] as const

const STUDIO_IDEA_POINTS = [
  { label: 'Распознал', value: 'кроссовки для города' },
  { label: 'Предложил', value: 'светлая сцена, движение, чистая витрина' },
  { label: 'Выделил', value: 'комфорт, материалы, стиль на каждый день' },
] as const

const VALUE_POINTS = [
  { title: 'Не ждать дизайнера для каждой акции', desc: 'Быстро соберите новую подачу, когда меняется сезон, скидка или первая картинка.' },
  { title: 'Тестировать визуальные гипотезы', desc: 'Проверяйте несколько обложек и акцентов, не начиная каждый вариант с нуля.' },
  { title: 'Держать единый стиль', desc: 'Повторяйте удачную подачу для новых товаров, категорий и серий.' },
  { title: 'Работать не только с маркетплейсами', desc: 'Используйте карточки, баннеры, модельные фото и видео для рекламы и соцсетей.' },
] as const



type CreditPack = { code: string; title: string; credits: number; priceMinor: number; oldPriceMinor?: number | null; currency: string }

type HeroShowcaseBase = {
  title: string
  tag: string
  className: string
  meta?: string
}

type HeroShowcaseItem =
  | (HeroShowcaseBase & { type: 'reveal'; before: string; after: string })
  | (HeroShowcaseBase & { type?: 'static'; img: string })

const HERO_SHOWCASE: HeroShowcaseItem[] = [
    {
    type: 'reveal',
    title: 'Инфографика товара',
    tag: '[NEW] Инфографика',
    before: 'https://storage.yandexcloud.net/ai-previews/all/preview_bads3_input.webp',
    after: 'https://storage.yandexcloud.net/ai-previews/all/preview_bads3.webp',
    className: 'hero-card--secondary',
    meta: 'Привлекательная карточка товара',
  },
  {
    type: 'reveal',
    title: 'Серия слайдов',
    tag: '[NEW] Серия слайдов',
    before: 'https://storage.yandexcloud.net/ai-previews/all/product_slides_input_2.webp',
    after: 'https://storage.yandexcloud.net/ai-previews/all/product_slides_result_1.webp',
    className: 'hero-card--secondary',
    meta: 'Создайте за один клик сразу набор слайдов в единном стиле',
  },
  {
    type: 'reveal',
    title: 'Баннер товара',
    tag: 'Карточка товара',
    before: 'https://storage.yandexcloud.net/ai-previews/all/input_45.webp',
    after: 'https://storage.yandexcloud.net/ai-previews/all/result_45.webp',
    className: 'hero-card--secondary',
    meta: 'Привлекательная карточка товара',
  },
  {
    type: 'reveal',
    title: 'Примерка на модели',
    tag: 'Генерация на модели',
    className: 'hero-card--primary',
    before: 'https://storage.yandexcloud.net/ai-previews/all/input_33.jpg',
    after: 'https://storage.yandexcloud.net/ai-previews/all/result_33.webp',
    meta: 'Высокая точность даже сложных узоров',
  },
  {
    type: 'reveal',
    title: 'Рекламный баннер',
    tag: 'Генерация на модели',
    before: 'https://storage.yandexcloud.net/ai-previews/all/input_354.webp',
    after: 'https://storage.yandexcloud.net/ai-previews/all/result_354.webp',
    className: 'hero-card--tertiary',
    meta: 'Идеально для рекламных баннеров',
  },
]
const HERO_STATIC_CARD_DURATION_MS = 4600
const HERO_REVEAL_INTRO_DELAY_MS = 1200
const HERO_REVEAL_AFTER_HOLD_MS = 2600
const HERO_REVEAL_TOTAL_DURATION_MS = HERO_REVEAL_INTRO_DELAY_MS + HERO_REVEAL_AFTER_HOLD_MS

const FAQ_ITEMS = [
  {
    value: 'how-it-works',
    question: 'Как работает генерация карточек товара?',
    answer: 'Вы загружаете фото вашего товара, выбираете стиль или шаблон, и Fravai автоматически создает профессиональную карточку товара с инфографикой, удаляя фон и добавляя нужные элементы.'
  },
  {
    value: 'marketplaces',
    question: 'Подходит ли это для Wildberries и Ozon?',
    answer: 'Да. Мы учитываем требования и форматы популярных маркетплейсов - Wildberries, Ozon, Яндекс.Маркет - чтобы карточки выглядели корректно и проходили модерацию.'
  },
  {
    value: 'brand-style',
    question: 'Можно ли сохранить единый стиль бренда?',
    answer: 'Да. Вы можете использовать единый стиль для всех карточек товаров - фирменные цвета, шрифты и подачу. Это помогает сохранить узнаваемость бренда и целостный визуал.'
  },
  {
    value: 'speed',
    question: 'Сколько времени занимает генерация карточки товара?',
    answer: 'В среднем генерация занимает от нескольких секунд до пары минут - результат вы получаете практически мгновенно.'
  },
  {
    value: 'free',
    question: 'Можно ли сделать инфографику бесплатно?',
    answer: 'Да. При регистрации вы получаете бесплатные кредиты, которых хватает на создание нескольких вариантов карточек и инфографики.'
  },
  {
    value: 'no-design-skills',
    question: 'Нужны ли навыки дизайна для работы с сервисом?',
    answer: 'Нет. Сервис полностью автоматизирован - достаточно загрузить фото товара и выбрать стиль.'
  },
  {
    value: 'editing',
    question: 'Можно ли отредактировать результат после генерации?',
    answer: 'Да. Вы можете изменить стиль, текстовые блоки или пересобрать карточку, создав несколько вариантов.'
  },
  {
    value: 'ab-test',
    question: 'Можно ли делать несколько вариантов для A/B тестирования?',
    answer: 'Да. Fravai позволяет создавать несколько версий одной карточки товара и выбрать наиболее эффективную.'
  },
  {
    value: 'formats',
    question: 'В каком формате я получу результат?',
    answer: 'Вы получаете изображения в высоком разрешении PNG или JPG, полностью готовые к загрузке на маркетплейсы.'
  }
]

type HeroCardRuntimeProps = {
  position: number
  isActive: boolean
}

function HeroShowcaseDeck({ items }: { items: HeroShowcaseItem[] }) {
  const [order, setOrder] = useState(() => items.map((_, idx) => idx))
  const advance = useCallback(() => {
    setOrder((prev) => {
      if (prev.length <= 1) return prev
      const [first, ...rest] = prev
      return [...rest, first]
    })
  }, [])

  useEffect(() => {
    if (!order.length) return
    const activeItem = items[order[0]]
    if (!activeItem || activeItem.type === 'reveal') return
    const timeout = window.setTimeout(() => advance(), HERO_STATIC_CARD_DURATION_MS)
    return () => window.clearTimeout(timeout)
  }, [order, items, advance])

  const handleRevealCycle = useCallback(() => {
    advance()
  }, [advance])

  const maxVisible = Math.min(2, items.length)
  const handleAdvance = useCallback(() => {
    advance()
  }, [advance])

  return (
    <div className="hero-showcase reveal reveal--up">
      {order.map((itemIndex, position) => {
        if (position >= maxVisible) return null
        const item = items[itemIndex]
        const isActive = position === 0
        const commonProps = { position, isActive }
        return item.type === 'reveal' ? (
          <HeroRevealCard
            key={`${item.title}-${itemIndex}`}
            {...item}
            {...commonProps}
            onCycleReady={handleRevealCycle}
            onAdvance={isActive ? handleAdvance : undefined}
          />
        ) : (
          <HeroStaticCard
            key={`${item.title}-${itemIndex}`}
            {...item}
            {...commonProps}
            onAdvance={isActive ? handleAdvance : undefined}
          />
        )
      })}
    </div>
  )
}


export default function LandingPage() {
  useRevealOnScroll();
  const [topbarSolid, setTopbarSolid] = useState(false)
  const heroRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const heroElement = heroRef.current
    if (!heroElement || typeof IntersectionObserver === 'undefined') {
      setTopbarSolid(false)
      return
    }

    const io = new IntersectionObserver(
      ([entry]) => setTopbarSolid(!entry.isIntersecting),
      { root: null, rootMargin: `-${EXAMPLES_MARGIN}px 0px 0px 0px`, threshold: 0 }
    )
    io.observe(heroElement)
    return () => io.disconnect()
  }, [])

  const [packs, setPacks] = useState<CreditPack[]>([])
  const [packsErr, setPacksErr] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    api.listCreditPacks('RUB').then((res) => { if (!cancelled) setPacks(res) }).catch((e: any) => { if (!cancelled)
        setPacksErr('Не удалось загрузить тарифы. Скорее всего технические работы.') })
    return () => { cancelled = true }
  }, [])
  const visiblePacks = packs.slice(0, 4)
  return (
    <>
      <Seo
        title="Генерация карточек товара и инфографики"
        description="AI-сервис для создания продающих карточек товара и инфографики для Wildberries и Ozon. Генерируйте контент и примеряйте одежду на моделях за минуты."
        canonical="/"
        keywords={LANDING_KEYWORDS}
        openGraph={{
          type: 'website',
          image: {
            url: DEFAULT_OG_IMAGE,
            alt: 'Пример AI-контента для карточек и баннеров Fravai',
          },
        }}
        jsonLd={LANDING_JSON_LD}
      />
      <Box component="main">
        <Topbar solid={topbarSolid} />

        {/* HERO */}
        <Box id="home" component="section" className="landing-hero" style={{ scrollMarginTop: TOPBAR_OFFSET }} ref={heroRef}>
          <div className="landing-hero__bg" aria-hidden />
          <Container size="lg">
            <div className="landing-hero__layout">
              <div className="landing-hero__content">
                <Title order={1} className="landing-hero__title">
                  Генерация <span className="hero-accent">карточек товара</span> и <span className="hero-accent">инфографики</span> для маркетплейсов
                </Title>
                <Text className="landing-hero__subtitle" mt="sm">
                  <span className="brand-wordmark-inline">{BRAND_NAME}</span> помогает создавать карточки, инфографику, серии слайдов, A/B варианты и фото на моделях без фотостудий.
                </Text>
                <Group mt="xl" gap="sm" wrap="wrap" className="landing-hero__actions">
                  <Button component={Link} to="/auth" size="md" radius="xl" className="hero-cta" leftSection={<IconSparkles size={18} />}>
                    Попробовать бесплатно
                  </Button>
                  <Button component="a" href="#examples" size="md" variant="outline" radius="xl" className="hero-link" rightSection={<IconArrowRight size={18} />}>
                    Смотреть примеры
                  </Button>
                </Group>
              </div>
              <div className="landing-hero__visual">
                <div className="hero-orb hero-orb--primary" />
                <div className="hero-orb hero-orb--secondary" />
                <HeroShowcaseDeck items={HERO_SHOWCASE} />
              </div>
              <div className="landing-hero__stats">
                <div className="hero-stats">
                  {HERO_STATS.map((stat) => (
                    <div key={stat.value} className="hero-stat">
                      <span className="hero-stat__value">
                        <CountUpValue value={stat.value} />
                      </span>
                      <span className="hero-stat__label">{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </Box>

        {/* LOGO GRID */}
        <Section py="sm" className="landing-logo-section landing-flow-section" fluid>
          <div className="logo-ticker__head">
            <Text size="md" c="dimmed">Нам доверяют продавцы из:</Text>
          </div>
          <div className="logo-grid" aria-label="Маркетплейсы">
            {MARKET_LOGOS.map((brand, idx) => (
              <div key={`${brand.name}-${idx}`} className="logo-grid__item" style={{ borderColor: brand.color }}>
                <span className="logo-grid__icon" style={{ color: brand.color }}>
                  {brand.iconUrl ? (
                    <img src={brand.iconUrl} alt="" className="logo-grid__icon-image" loading="lazy" decoding="async" />
                  ) : (
                    brand.short
                  )}
                </span>
                <span className="logo-grid__name">{brand.name}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* EXAMPLES */}
        <Section id="examples" style={{ marginTop: EXAMPLES_MARGIN }} className="examples-section landing-flow-section" fluid>
          <Container size="lg">
            <div className="section-header section-header--center">
              <Title order={2}>Примеры по задачам</Title>
              <Text size="xs" style={{ opacity: 0.7 }} c="dimmed">
                Карточки, инфографика и серии, созданные через Fravai.
              </Text>
            </div>
          </Container>
          <ExamplesCarousel items={examplesCarousel} />
          <ShowcaseTabs showcases={ELASTIC_SHOWCASES} />
        </Section>

        {/* TOOLS */}
        <Section id="tools" py="xl" className="tool-menu-section landing-flow-section" size="xl">
          <div className="tool-menu">
            <div className="tool-menu__lead reveal reveal--up">
              <Title order={2}>Что можно сделать в Fravai</Title>
              <Text c="dimmed">
                Выберите конкретную задачу для товарного визуала: от первой карточки до теста готового дизайна.
              </Text>
              <Button component={Link} to="/auth" radius="xl" className="tool-menu__button" rightSection={<IconArrowRight size={18} />}>
                Попробовать бесплатно
              </Button>
            </div>
            <div className="tool-menu__list">
              {SERVICE_TOOLS.map((tool, index) => (
                <article key={tool.title} className="tool-menu__item reveal reveal--up" style={{ ["--reveal-delay" as any]: `${index * 40}ms` }}>
                  <span className="tool-menu__index">{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <span className="tool-menu__label">{tool.label}</span>
                    <Title order={4}>{tool.title}</Title>
                    <Text c="dimmed">{tool.desc}</Text>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </Section>

        {/* IDEA HELP */}
        <Section py="xl" className="studio-assist-section landing-flow-section" size="xl">
          <div className="studio-assist-card reveal reveal--scale">
            <div className="studio-assist-card__copy">
              <Title order={2}>Виртуальный дизайнер для вашей карточки</Title>
              <Text c="dimmed">
                Не нужно разбираться в дизайне, сценах и настройках. Система понимает товар, предлагает визуальную идею и помогает собрать результат.
              </Text>
              <div className="studio-assist-card__checks">
                {STUDIO_HELPERS.map((item) => <span key={item}>{item}</span>)}
              </div>
            </div>
            <div className="studio-idea-board" aria-label="Как виртуальный дизайнер помогает собрать идею">
              <div className="studio-idea-board__top">
                <span>Визуальная идея</span>
                <strong>От фото товара к понятной сцене</strong>
              </div>
              <div className="studio-idea-board__rows">
                {STUDIO_IDEA_POINTS.map((item) => (
                  <div key={item.label} className="studio-idea-board__row">
                    <span>{item.label}</span>
                    <p>{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="studio-idea-board__result">
                <span>На выходе</span>
                <p>Готовое направление для карточки: композиция, преимущества, стиль и понятный следующий шаг.</p>
              </div>
            </div>
          </div>
        </Section>

        {/* ENTRY POINTS */}
        <Section py="xl" className="entry-section landing-flow-section" size="xl">
          <div className="entry-layout">
            <div className="entry-copy reveal reveal--up">
              <Title order={2}>Можно прийти с любой задачей</Title>
              <Text c="dimmed">
                Fravai не заставляет идти по сценарию от фото до видео. Сервис открывается с выбора нужного инструмента.
              </Text>
            </div>
            <div className="entry-list">
              {ENTRY_SCENARIOS.map((item, index) => (
                <article key={item.title} className="entry-card reveal reveal--up" style={{ ["--reveal-delay" as any]: `${index * 55}ms` }}>
                  <span className="entry-card__index">{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <Title order={4}>{item.title}</Title>
                    <Text c="dimmed">{item.desc}</Text>
                  </div>
                  <span className="entry-card__tools">{item.tools}</span>
                </article>
              ))}
            </div>
          </div>
        </Section>

        {/* VALUE */}
        <Section py="xl" className="value-section landing-flow-section" size="xl">
          <div className="value-grid">
            <div className="value-lead reveal reveal--up">
              <Title order={2}>Где это реально экономит время</Title>
              <Text c="dimmed">Без выдуманных процентов: сервис нужен там, где визуал приходится быстро выпускать, менять и тестировать.</Text>
            </div>
            {VALUE_POINTS.map((item, index) => (
              <article key={item.title} className="value-card reveal reveal--up" style={{ ["--reveal-delay" as any]: `${index * 50}ms` }}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <Title order={4}>{item.title}</Title>
                <Text c="dimmed">{item.desc}</Text>
              </article>
            ))}
          </div>
        </Section>

        {/* PACKS */}
        <Section id="packs" py="xl" className="pricing-section landing-flow-section" size="xl">
          <div className="section-header section-header--center">
            {/* <BadgeSoft tone="iris">Удобное пополнение</BadgeSoft> */}
            <Title order={2}>Тарифы</Title>
            <Text c="dimmed" mt={4} className="pricing-section__subtitle">Пополняйте баланс в один клик</Text>
          </div>
          {packsErr && <Text c="red" mt={1} size="sm">{packsErr}</Text>}
          <SimpleGrid cols={{ base: 1, sm: 2, md: visiblePacks.length > 3 ? 4 : 3 }} spacing="lg" mt="xl">
            {visiblePacks.map((pack, i) => (
              <PackCard key={pack.code} pack={pack} delay={i * 80} />
            ))}
            {packs.length === 0 && !packsErr && (
              <Card withBorder radius="xl" p="xl" className="grid-card pricing-card pricing-card--loading" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                <Text c="dimmed">Загрузка…</Text>
              </Card>
            )}
          </SimpleGrid>
          <div className="pricing-bonus">
            <div className="pricing-bonus__icon" aria-hidden>✦</div>
            <div className="pricing-bonus__content">
              <Text fw={700} className="pricing-bonus__title">Бесплатные кредиты для новых пользователей</Text>
              <Text size="sm" className="pricing-bonus__text">50 кредитов при регистрации - хватит, чтобы протестировать сервис</Text>
            </div>
            <Button component={Link} to="/auth" radius="xl" size="sm" className="pricing-bonus__button">
              Попробовать бесплатно
            </Button>
          </div>
        </Section>

        {/* FAQ */}
        <Section id="faq" py="xl">
          <div className="section-header section-header--center">
            <BadgeSoft tone="neutral">FAQ</BadgeSoft>
            <Title order={2}>Частые вопросы</Title>
          </div>
          <Container size="sm" mt="xl">
            <Accordion variant="separated" radius="lg" chevronPosition="right" styles={{
              item: {
                backgroundColor: '#ffffff',
                borderColor: 'rgba(148, 163, 184, 0.24)',
              },
              control: {
                color: '#0f172a',
              },
              panel: {
                color: 'rgba(15, 23, 42, 0.72)',
                lineHeight: 1.6,
              },
            }}>
              {FAQ_ITEMS.map((item) => (
                <Accordion.Item key={item.value} value={item.value} className="faq-item">
                  <Accordion.Control>{item.question}</Accordion.Control>
                  <Accordion.Panel>{item.answer}</Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Container>
        </Section>

        {/* CTA */}
        <Section py="lg">
          <Card withBorder radius="xl" p="xl" className="cta-card">
            <div className="section-header section-header--center">
              <Title order={2}>Готовы ускорить контент-продакшн?</Title>
              <Text c="dimmed" mt="xs">Начните выпускать карточки и баннеры в разы быстрее.</Text>
            </div>
            <Group justify="center" mt="lg">
              <Button component={Link} to="/auth" size="md" radius="xl" className="cta-card__button">
                Попробовать бесплатно
              </Button>
            </Group>
          </Card>
        </Section>

        <SiteFooter />
      </Box>
    </>
  )
}

function BadgeSoft({ children, tone = 'neutral', className }: { children: React.ReactNode; tone?: AccentTone; className?: string }) {
  const classes = ['badge-soft', `badge-soft--${tone}`, className].filter(Boolean).join(' ')
  return (
    <Box component="span" className={classes}>
      {children}
    </Box>
  )
}

type HeroStaticCardProps = HeroShowcaseBase & { img: string } & HeroCardRuntimeProps & { onAdvance?: () => void }

function HeroStaticCard({ title, tag, img, className, meta, position, isActive, onAdvance }: HeroStaticCardProps) {
  const cardClassName = ['hero-card', className].filter(Boolean).join(' ')
  const imageLoading = isActive ? 'eager' : 'lazy'
  const imageDecoding = isActive ? 'sync' : 'async'
  const imagePriority = isActive ? 'high' : 'low'
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onAdvance) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onAdvance()
    }
  }
  return (
    <div
      className={cardClassName}
      data-position={position}
      data-active={isActive ? 'true' : 'false'}
      onClick={onAdvance}
      onKeyDown={handleKeyDown}
      role={onAdvance ? 'button' : undefined}
      tabIndex={onAdvance ? 0 : -1}
      aria-label={onAdvance ? `Следующий пример: ${title}` : undefined}
    >
      <div className="hero-card__media">
        <img
          src={img}
          alt={title}
          width={800}
          height={1000}
          loading={imageLoading}
          decoding={imageDecoding}
          fetchPriority={imagePriority}
        />
      </div>
      <div className="hero-card__meta">
        <span className="hero-card__tag">{tag}</span>
        <span className="hero-card__title">{title}</span>
        {meta && <span className="hero-card__meta-note">{meta}</span>}
      </div>
    </div>
  )
}

interface HeroRevealCardProps extends HeroShowcaseBase, HeroCardRuntimeProps {
  before: string
  after: string
  onCycleReady: () => void
  onAdvance?: () => void
}

function HeroRevealCard({ title, tag, before, after, className, meta, position, isActive, onCycleReady, onAdvance }: HeroRevealCardProps) {
  const [phase, setPhase] = useState<'before' | 'after'>('before')
  const timersRef = useRef<number[]>([])
  const cycleNotifiedRef = useRef(false)
  const [timelineKey, setTimelineKey] = useState(0)

  useEffect(() => {
    timersRef.current.forEach((timerId) => window.clearTimeout(timerId))
    timersRef.current = []
    cycleNotifiedRef.current = false

    if (!isActive) {
      setPhase((current) => (current === 'before' ? current : 'before'))
      return
    }

    setPhase('before')
    setTimelineKey((value) => value + 1)

    const toAfterId = window.setTimeout(() => {
      setPhase('after')
      const advanceId = window.setTimeout(() => {
        if (!cycleNotifiedRef.current) {
          cycleNotifiedRef.current = true
          onCycleReady()
        }
      }, HERO_REVEAL_AFTER_HOLD_MS)
      timersRef.current.push(advanceId)
    }, HERO_REVEAL_INTRO_DELAY_MS)

    timersRef.current.push(toAfterId)

    return () => {
      timersRef.current.forEach((timerId) => window.clearTimeout(timerId))
      timersRef.current = []
    }
  }, [isActive, onCycleReady])

  const cardClassName = ['hero-card', 'hero-reveal', className].filter(Boolean).join(' ')
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onAdvance) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onAdvance()
    }
  }

  return (
    <div
      className={cardClassName}
      data-position={position}
      data-active={isActive ? 'true' : 'false'}
      data-phase={phase}
      onClick={onAdvance}
      onKeyDown={handleKeyDown}
      role={onAdvance ? 'button' : undefined}
      tabIndex={onAdvance ? 0 : -1}
      aria-label={onAdvance ? `Следующий пример: ${title}` : undefined}
    >
      <div className="hero-reveal__stage">
        <img
          src={before}
          alt={`${title} - исходник`}
          className="hero-reveal__image hero-reveal__image--before"
          width={900}
          height={900}
          loading={isActive ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={isActive ? 'high' : 'low'}
        />
        <img
          src={after}
          alt={`${title} - результат`}
          className="hero-reveal__image hero-reveal__image--after"
          width={900}
          height={900}
          loading={isActive ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={isActive ? 'high' : 'low'}
        />
        <div className="hero-reveal__labels">
          <span className={`hero-reveal__badge ${phase === 'before' ? 'is-active' : ''}`}>Исходник</span>
          <span className={`hero-reveal__badge ${phase === 'after' ? 'is-active' : ''}`}>Результат</span>
        </div>
      </div>
      <div className="hero-card__meta">
        <span className="hero-card__tag">{tag}</span>
        <span className="hero-card__title">{title}</span>
        {meta && <span className="hero-card__meta-note">{meta}</span>}
      </div>
      <div className="hero-reveal__timeline" data-active={isActive ? 'true' : 'false'}>
        {isActive && (
          <span
            key={timelineKey}
            className="hero-reveal__timeline-bar"
            style={{ animationDuration: `${HERO_REVEAL_TOTAL_DURATION_MS}ms` }}
          />
        )}
      </div>
    </div>
  )
}

function ExamplesCarousel({ items }: { items: Array<{ img: string; title: string }> }) {
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])
  const plugins = useMemo(
    () => (prefersReducedMotion ? [] : [AutoScroll({ speed: 1.4, startDelay: 0 })]),
    [prefersReducedMotion]
  )
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', dragFree: true },
    plugins
  )

  useEffect(() => {
    const plugin = emblaApi?.plugins()?.autoScroll
    plugin?.play()
  }, [emblaApi])

  const handleMouseEnter = useCallback(() => {
    const plugin = emblaApi?.plugins()?.autoScroll
    plugin?.stop()
  }, [emblaApi])

  const handleMouseLeave = useCallback(() => {
    const plugin = emblaApi?.plugins()?.autoScroll
    plugin?.play()
  }, [emblaApi])

  return (
    <div className="examples-carousel" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className="examples-carousel__viewport" ref={emblaRef}>
        <div className="examples-carousel__container">
          {items.map((item, idx) => (
            <div className="examples-carousel__slide" key={`${item.title}-${idx}`}>
              <div className="examples-carousel__card">
                <img src={item.img} alt={item.title} width={600} height={800} loading="lazy" decoding="async" fetchPriority="low" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function ShowcaseTabs({ showcases }: { showcases: ElasticShowcase[] }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const activeShowcase = showcases[activeIndex] ?? showcases[0]
  const goNext = useCallback(() => {
    setActiveIndex((value) => (value + 1) % showcases.length)
  }, [showcases.length])

  if (!activeShowcase) return null

  return (
    <div className="showcase-tabs reveal reveal--up">
      <div className="showcase-tabs__nav" role="tablist" aria-label="Серии примеров">
        {showcases.map((showcase, index) => {
          const isActive = index === activeIndex
          return (
            <button
              key={showcase.id}
              type="button"
              className={`showcase-tabs__button ${isActive ? 'is-active' : ''}`}
              onClick={() => setActiveIndex(index)}
              role="tab"
              aria-selected={isActive}
            >
              <span>{showcase.title}</span>
              <small>{showcase.subtitle}</small>
            </button>
          )
        })}
      </div>
      <div className="showcase-tabs__panel" role="tabpanel" aria-label={activeShowcase.title}>
        <ElasticResultsShowcase
          key={activeShowcase.id}
          showcase={activeShowcase}
          index={0}
          onCycleComplete={goNext}
        />
      </div>
    </div>
  )
}

function ElasticResultsShowcase({
  showcase,
  index,
  onCycleComplete,
}: {
  showcase: ElasticShowcase
  index: number
  onCycleComplete?: () => void
}) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [isInView, setIsInView] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const pointerStartXRef = useRef<number | null>(null)
  const resumeTimerRef = useRef<number | null>(null)
  const slides = useMemo(() => [showcase.source, ...showcase.results], [showcase])
  const totalSlides = slides.length

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current === null) return
    window.clearTimeout(resumeTimerRef.current)
    resumeTimerRef.current = null
  }, [])

  const pauseBriefly = useCallback((duration = 1600) => {
    clearResumeTimer()
    setIsPaused(true)
    resumeTimerRef.current = window.setTimeout(() => {
      setIsPaused(false)
      resumeTimerRef.current = null
    }, duration)
  }, [clearResumeTimer])

  useEffect(() => clearResumeTimer, [clearResumeTimer])

  useEffect(() => {
    const target = stageRef.current
    if (!target) return
    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setIsInView(true)
      return
    }

    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          obs.unobserve(entry.target)
        }
      })
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.35 })

    io.observe(target)
    return () => io.disconnect()
  }, [prefersReducedMotion])

  const goForward = useCallback(() => {
    if (activeIndex >= totalSlides - 1) {
      setActiveIndex(0)
      onCycleComplete?.()
      return
    }
    setActiveIndex((value) => (value + 1) % totalSlides)
  }, [activeIndex, onCycleComplete, totalSlides])

  const goBackward = useCallback(() => {
    setActiveIndex((value) => (value - 1 + totalSlides) % totalSlides)
  }, [totalSlides])

  useEffect(() => {
    if (!isInView || isPaused || prefersReducedMotion || totalSlides <= 1) return

    const timerId = window.setTimeout(() => {
      goForward()
    }, 4200)

    return () => window.clearTimeout(timerId)
  }, [goForward, isInView, isPaused, prefersReducedMotion, activeIndex, totalSlides])

  const getOffset = useCallback((itemIndex: number) => {
    let offset = itemIndex - activeIndex
    const half = totalSlides / 2
    if (offset > half) offset -= totalSlides
    if (offset < -half) offset += totalSlides
    return offset
  }, [activeIndex, totalSlides])

  const getCardStyle = useCallback((itemIndex: number): React.CSSProperties => {
    const offset = getOffset(itemIndex)
    const absOffset = Math.abs(offset)
    const direction = Math.sign(offset)
    const isVisible = absOffset <= 2
    const x = direction * (absOffset === 1 ? 48 : 74)
    const z = absOffset === 0 ? 0 : absOffset === 1 ? -55 : -180
    const rotateY = direction * (absOffset === 1 ? -28 : -46)
    const scale = absOffset === 0 ? 1 : absOffset === 1 ? 0.88 : 0.7
    const opacity = absOffset === 0 ? 1 : absOffset === 1 ? 0.82 : 0.2

    return {
      opacity: isVisible ? opacity : 0,
      pointerEvents: absOffset === 0 ? 'auto' : 'none',
      transform: `translateX(${x}%) translateZ(${z}px) rotateY(${rotateY}deg) scale(${scale})`,
      zIndex: 10 - absOffset,
    }
  }, [getOffset])

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    clearResumeTimer()
    setIsPaused(true)
    pointerStartXRef.current = event.clientX
  }, [clearResumeTimer])

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const startX = pointerStartXRef.current
    pointerStartXRef.current = null
    if (typeof startX !== 'number') {
      pauseBriefly(900)
      return
    }

    const deltaX = event.clientX - startX
    if (Math.abs(deltaX) < 36) {
      pauseBriefly(900)
      return
    }
    if (deltaX < 0) goForward()
    else goBackward()
    pauseBriefly()
  }, [goBackward, goForward, pauseBriefly])

  return (
    <article
      className="elastic-showcase"
      style={{ ["--reveal-delay" as any]: `${index * 70}ms` }}
    >
      <div className="elastic-showcase__header">
        <Title order={3} className="elastic-showcase__title">{showcase.title}</Title>
        <Text c="dimmed" className="elastic-showcase__subtitle">{showcase.subtitle}</Text>
      </div>

      <div
        ref={stageRef}
        className={`elastic-showcase__viewport ${isInView ? 'is-inview' : ''} ${prefersReducedMotion ? 'is-reduced' : ''}`}
      >
        <div
          className="elastic-showcase__stage"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={() => {
            pointerStartXRef.current = null
            pauseBriefly(900)
          }}
        >
          {slides.map((item, itemIndex) => {
            const isSource = itemIndex === 0
            const isActive = itemIndex === activeIndex
            return (
              <div
                key={`${showcase.id}-${item.title}-${itemIndex}`}
                className={`elastic-showcase__wheel-card ${isActive ? 'is-active' : ''}`}
                style={getCardStyle(itemIndex)}
                aria-hidden={!isActive}
              >
                <img src={item.img} alt={item.title} width={600} height={800} loading="lazy" decoding="async" fetchPriority={isActive ? 'high' : 'low'} />
                {isSource && <span className="elastic-showcase__chip">Исходник</span>}
              </div>
            )
          })}

          <button
            type="button"
            className="elastic-showcase__nav elastic-showcase__nav--prev"
            onClick={() => {
              goBackward()
              pauseBriefly()
            }}
            aria-label="Предыдущая карточка"
          >
            <IconChevronLeft size={22} stroke={2} />
          </button>
          <button
            type="button"
            className="elastic-showcase__nav elastic-showcase__nav--next"
            onClick={() => {
              goForward()
              pauseBriefly()
            }}
            aria-label="Следующая карточка"
          >
            <IconChevronRight size={22} stroke={2} />
          </button>
        </div>
      </div>
    </article>
  )
}


function PackCard({ pack, delay = 0 }: { pack: CreditPack; delay?: number }) {
  const cardClassName = ['grid-card', 'pricing-card'].join(' ')
  const creditsValue = Math.round(Number(pack.credits || 0))
  const approxImages = Math.max(1, Math.ceil(pack.credits / IMAGE_GENERATION_COST_CREDITS))
  const approxVideos = Math.max(1, Math.ceil(pack.credits / VIDEO_GENERATION_COST_CREDITS))
  const badge = (() => {
    const code = String(pack.code || '').toLowerCase()
    if (code === 'plus') return { label: 'ХИТ ПРОДАЖ', tone: 'hot' as const }
    if (code === 'pro') return { label: 'ЛУЧШИЙ ВЫБОР', tone: 'best' as const }
    return null
  })()
  const subtitle = (() => {
    const code = String(pack.code || '').toLowerCase()
    if (code === 'basic') return 'Лёгкий старт'
    if (code === 'plus') return 'Поток контента'
    if (code === 'pro') return 'Полный контроль'
    if (code === 'ultra') return 'Для масштаба'
    const title = String(pack.title || '').toLowerCase()
    if (title.includes('старт') || title.includes('basic')) return 'Лёгкий старт'
    if (title.includes('креатор') || title.includes('plus')) return 'Поток контента'
    if (title.includes('студия') || title.includes('pro')) return 'Полный контроль'
    if (title.includes('бизнес') || title.includes('ultra')) return 'Для масштаба'
    return 'Пакет кредитов'
  })()
  return (
    <Box className="pack-card">
      {badge && (
        <BadgeSoft tone="sunrise" className={`pack-card__badge pack-card__badge--${badge.tone}`} >
          {badge.label}
        </BadgeSoft>
      )}
      <Card withBorder radius="lg" p="lg" className={cardClassName} style={{ animationDelay: `${delay}ms` }}>
        <div className="pricing-card__top">
          <div className="pricing-card__head">
            <Title order={4} className="pricing-card__title">{pack.title}</Title>
            <Text c="dimmed" size="sm" className="pricing-card__subtitle">{subtitle}</Text>
          </div>
          <div className="pricing-card__price">
            <span className="pricing-card__current">{fmtMoneyInteger(pack.priceMinor, pack.currency)}</span>
            {typeof pack.oldPriceMinor === 'number' && (
              <span className="pricing-card__old">{fmtMoneyInteger(pack.oldPriceMinor, pack.currency)}</span>
            )}
          </div>
          <div className="pricing-card__credits">
            <span className="pricing-card__icon pricing-card__icon--spark" aria-hidden>
              <IconSparkles size={13} stroke={2} />
            </span>
            <span className="pricing-card__credits-value">{creditsValue}</span>
            <span className="pricing-card__credits-label">кредитов</span>
          </div>
        </div>
        <div className="pricing-card__divider" />
        <div className="pricing-card__bottom">
          <div className="pricing-card__metrics">
            <div className="pricing-card__metric">
              <span className="pricing-card__icon pricing-card__icon--photo" aria-hidden>
                <IconPhoto size={13} stroke={2} />
              </span>
              <span>{approxImages} изображений</span>
            </div>
            <div className="pricing-card__metric">
              <span className="pricing-card__icon pricing-card__icon--video" aria-hidden>
                <IconVideo size={13} stroke={2} />
              </span>
              <span>{approxVideos} видео</span>
            </div>
          </div>
        </div>
        <Button component={Link} to={`/auth?returnTo=/topup`} radius="xl" fullWidth className="pricing-card__button pricing-card__button--primary">
          Пополнить
        </Button>
      </Card>
    </Box>
  )
}
type RoundingMode = 'floor' | 'round' | 'ceil';

function getCurrencyExponent(currency: string): number {
  // Сколько знаков после запятой у валюты по ISO-4217
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency })
    .resolvedOptions().minimumFractionDigits ?? 0;
}

function toMajorInteger(minor: number, currency: string, mode: RoundingMode = 'floor'): number {
  if (!Number.isSafeInteger(minor)) throw new Error('priceMinor is not a safe integer');
  const exp = getCurrencyExponent(currency);
  const factor = Math.pow(10, exp);

  // Чистая целочисленная математика
  const q = Math.trunc(minor / factor);      // частное (целая часть)
  const r = minor % factor;                   // остаток (субединицы)

  if (mode === 'floor') return q;
  if (mode === 'ceil') return r === 0 ? q : q + 1;
  // 'round'
  return r >= factor / 2 ? q + 1 : q;
}

export function fmtMoneyInteger(minor: number, currency: string, locale = 'ru-RU', mode: RoundingMode = 'floor') {
  const major = toMajorInteger(minor, currency, mode); // целое количество основных единиц
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(major);
}
